import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Upload, Loader2 } from 'lucide-react';
import { MARKER_STYLES, MARKER_ICONS } from '@/lib/markerIcons';
import { storage } from '@/lib/trackizy';
import { downscaleImageFile } from '@/lib/downscaleImage';

const STATUS_COLORS = [
  { key: 'moving', color: '#10b981' },
  { key: 'stopped', color: '#ef4444' },
  { key: 'idle', color: '#f59e0b' },
  { key: 'offline', color: '#94a3b8' },
];

/** Render an inline SVG from an icon definition */
function IconSvg({ icon, size = 24, color = 'currentColor', strokeWidth = 1.5 }) {
  if (!icon) return null;
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke={color}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {icon.paths.map((d, i) => (
        <path key={i} d={d} />
      ))}
      {icon.circles.map((c, i) => (
        <circle key={i} cx={c.cx} cy={c.cy} r={c.r} />
      ))}
    </svg>
  );
}

/** Small arrow SVG for arrow-style preview */
function ArrowPreview({ color, size = 20 }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size}>
      <polygon
        points="12,2 20,20 12,16 4,20"
        fill={color}
        stroke="white"
        strokeWidth="1"
      />
    </svg>
  );
}

/** Ring + pointer preview */
function RingPreview({ color, icon, size = 20 }) {
  return (
    <svg viewBox="0 0 32 40" width={size} height={size * 1.25}>
      {/* Pointer triangle */}
      <polygon points="16,38 10,26 22,26" fill={color} />
      {/* Ring circle */}
      <circle cx="16" cy="16" r="14" fill={color} stroke="white" strokeWidth="2" />
      {/* Inner white circle */}
      <circle cx="16" cy="16" r="10" fill="white" />
      {/* Icon inside */}
      {icon && (
        <g transform="translate(6,6) scale(0.833)">
          {icon.paths.map((d, i) => (
            <path key={i} d={d} fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          ))}
          {icon.circles.map((c, i) => (
            <circle key={i} cx={c.cx} cy={c.cy} r={c.r} fill="none" stroke={color} strokeWidth="1.8" />
          ))}
        </g>
      )}
    </svg>
  );
}

export default function MapMarkerPicker({
  open,
  onClose,
  currentStyle = 'arrow',
  currentIcon = null,
  currentCustomUrl = null,
  onSave,
  deviceId,
}) {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);

  const [selectedStyle, setSelectedStyle] = useState(currentStyle);
  const [selectedIcon, setSelectedIcon] = useState(currentIcon);
  const [selectedCustomUrl, setSelectedCustomUrl] = useState(currentCustomUrl);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  if (!open) return null;

  const handleSave = () => {
    onSave({
      style: selectedStyle,
      icon: selectedStyle === 'arrow' ? null : selectedIcon,
      customUrl: selectedIcon === 'custom' ? selectedCustomUrl : null,
    });
    onClose();
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadError(null);

    // Validate type
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['png', 'svg'].includes(ext)) return;

    // Marker icons draw at 36 px; 256 px keeps a crisp retina marker, and a
    // phone-camera PNG shrinks to a few KB instead of being refused below.
    const prepared = await downscaleImageFile(file, { maxDim: 256, maxBytes: 32 * 1024 });

    // Validate size (after the downscale — SVGs pass through untouched)
    if (prepared.size > 256 * 1024) {
      setUploadError(t('settings.iconFileTooLarge'));
      return;
    }

    setUploading(true);
    try {
      const uploadResult = await storage.upload('device-icons', prepared);
      const publicUrl = uploadResult?.url || storage.getPublicUrl(uploadResult?.path);

      setSelectedIcon('custom');
      setSelectedCustomUrl(publicUrl);
    } catch {
      setUploadError(t('settings.iconUploadError'));
    } finally {
      setUploading(false);
      // Reset input so same file can be re-selected
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const resolvedIcon = selectedIcon && selectedIcon !== 'custom'
    ? MARKER_ICONS.find((i) => i.key === selectedIcon)
    : null;

  // Default icon for previews when none selected
  const previewIcon = resolvedIcon || MARKER_ICONS[0];

  const uploadAvailable = true; // Storage is available via trackizy server

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
      <div
        className="bg-white dark:bg-slate-900 rounded-2xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-700">
          <h2 className="text-sm font-bold text-slate-900 dark:text-white">
            {t('settings.chooseMapMarker')}
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-4">
          {/* Style Selector */}
          <div>
            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-2 uppercase tracking-wide">
              {t('settings.markerStyle')}
            </p>
            <div className="grid grid-cols-3 gap-2">
              {MARKER_STYLES.map((style) => (
                <button
                  key={style.key}
                  onClick={() => setSelectedStyle(style.key)}
                  className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 transition-colors ${
                    selectedStyle === style.key
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/30'
                      : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                  }`}
                >
                  <div className="h-8 flex items-center justify-center">
                    {style.key === 'arrow' && <ArrowPreview color="#10b981" size={24} />}
                    {style.key === 'colored' && <IconSvg icon={MARKER_ICONS[0]} size={24} color="#10b981" />}
                    {style.key === 'ring' && <RingPreview color="#10b981" icon={MARKER_ICONS[0]} size={20} />}
                  </div>
                  <span className="text-[11px] font-medium text-slate-700 dark:text-slate-300">
                    {t(style.label)}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Icon Grid (hidden when arrow) */}
          {selectedStyle !== 'arrow' && (
            <>
              <div className="border-t border-slate-200 dark:border-slate-700" />
              <div>
                <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-2 uppercase tracking-wide">
                  {t('settings.chooseIcon')}
                </p>
                <div className="grid grid-cols-6 gap-1.5">
                  {MARKER_ICONS.map((icon) => (
                    <button
                      key={icon.key}
                      onClick={() => {
                        setSelectedIcon(icon.key);
                        setSelectedCustomUrl(null);
                      }}
                      className={`flex flex-col items-center gap-0.5 p-2 rounded-xl border-2 transition-colors ${
                        selectedIcon === icon.key
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/30'
                          : 'border-transparent hover:border-slate-200 dark:hover:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/50'
                      }`}
                    >
                      <IconSvg icon={icon} size={22} color="currentColor" />
                      <span className="text-[9px] text-slate-500 dark:text-slate-400 leading-tight text-center truncate w-full">
                        {t(icon.label)}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Upload Custom */}
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".png,.svg"
                  className="hidden"
                  onChange={handleFileSelect}
                />
                <button
                  onClick={() => {
                    if (uploadAvailable && !uploading) {
                      fileInputRef.current?.click();
                    }
                  }}
                  disabled={!uploadAvailable || uploading}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 border-dashed transition-colors ${
                    selectedIcon === 'custom' && selectedCustomUrl
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/30'
                      : 'border-slate-300 dark:border-slate-600'
                  } ${
                    !uploadAvailable
                      ? 'opacity-50 cursor-not-allowed'
                      : 'hover:border-slate-400 dark:hover:border-slate-500 cursor-pointer'
                  }`}
                >
                  <div className="shrink-0 w-10 h-10 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                    {uploading ? (
                      <Loader2 size={18} className="animate-spin text-blue-500" />
                    ) : selectedIcon === 'custom' && selectedCustomUrl ? (
                      <img src={selectedCustomUrl} alt="" className="w-6 h-6 object-contain" />
                    ) : (
                      <Upload size={18} className="text-slate-400" />
                    )}
                  </div>
                  <div className="text-start min-w-0">
                    <p className="text-xs font-medium text-slate-700 dark:text-slate-300">
                      {t('settings.uploadCustomIcon')}
                    </p>
                    <p className="text-[10px] text-slate-400 dark:text-slate-500">
                      {uploadAvailable
                        ? t('settings.uploadCustomIconHint')
                        : t('settings.uploadCustomDisabled')}
                    </p>
                  </div>
                </button>
                {uploadError && (
                  <p className="text-[11px] text-red-500 mt-1">{uploadError}</p>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-3 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
          {/* Preview */}
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-medium text-slate-500 dark:text-slate-400">
              {t('settings.preview')}:
            </span>
            <div className="flex items-center gap-1">
              {STATUS_COLORS.map(({ key, color }) => (
                <div key={key} className="w-6 h-6 flex items-center justify-center">
                  {selectedStyle === 'arrow' && <ArrowPreview color={color} size={18} />}
                  {selectedStyle === 'colored' && (
                    selectedIcon === 'custom' && selectedCustomUrl ? (
                      <img src={selectedCustomUrl} alt="" className="w-5 h-5 object-contain" style={{ filter: `drop-shadow(0 0 1px ${color})` }} />
                    ) : (
                      <IconSvg icon={previewIcon} size={18} color={color} strokeWidth={2} />
                    )
                  )}
                  {selectedStyle === 'ring' && (
                    selectedIcon === 'custom' && selectedCustomUrl ? (
                      <RingPreview color={color} icon={null} size={16} />
                    ) : (
                      <RingPreview color={color} icon={previewIcon} size={16} />
                    )
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Buttons */}
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="h-9 px-4 text-xs font-semibold rounded-xl bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors"
            >
              {t('settings.cancel')}
            </button>
            <button
              onClick={handleSave}
              className="h-9 px-4 text-xs font-semibold rounded-xl bg-blue-600 text-white hover:bg-blue-700 transition-colors"
            >
              {t('settings.save')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
